
import Vue from 'vue';

import EntityMetaDialog from '@/components/misc/EntityMetaDialog.vue';
import SimpleFiltersCard from '@/components/filters/SimpleFiltersCard.vue';
import ReadingGroupAggregationForm from '@/components/entities/view/ReadingGroupAggregationForm.vue';

import ViewService from '@/services/ViewService';
import { extractErrorMessage } from '@/utils/Util';


type EntityType = 'properties' | 'connectors' | 'devices' | 'readings';
type RequestType =
  | 'property-collection'
  | 'connector-collection'
  | 'device-collection'
  | 'reading-collection'
  | 'reading-aggregates-collection';

type AlertType = 'success' | 'info' | 'warning' | 'error';

export default Vue.extend({
  name: 'ViewEditDialog',

  props: {
    view: { type: Object, required: true },
  },

  components: {
    EntityMetaDialog,
    SimpleFiltersCard,
    ReadingGroupAggregationForm,
  },

  data: () => ({
    localdialog: false,
    localView: {} as any,

    requestConfigUi: {} as any,
    filtersKey: 0,

    valid: true,
    loading: false,

    alertVisible: false,
    alertText: '',
    alertType: 'error' as AlertType,

    justSaved: false,

    ENTITY_TYPES: [
      'properties',
      'connectors',
      'devices',
      'readings',
    ] as EntityType[],

    REQUEST_TYPES_BY_ENTITY: {
      properties: ['property-collection'],
      connectors: ['connector-collection'],
      devices: ['device-collection'],
      readings: ['reading-collection', 'reading-aggregates-collection'],
    } as Record<EntityType, RequestType[]>,
  }),

  methods: {
    resetAlertOnly() {
      this.alertVisible = false;
      this.alertText = '';
      this.alertType = 'error';
    },

    clear() {
      this.localView = {} as any;
      this.requestConfigUi = {};
      this.filtersKey = 0;

      this.resetAlertOnly();

      this.loading = false;
      this.valid = true;

      this.$nextTick(() => {
        const editFormRef = this.$refs.editForm as any;
        if (editFormRef?.resetValidation) editFormRef.resetValidation();
      });
    },

    dismiss() {
      if (this.loading) return;
      this.clear();
      this.localdialog = false;
    },

    validate() {
      const editFormRef = this.$refs.editForm as any;
      if (editFormRef?.validate && editFormRef.validate()) {
        this.save();
      }
    },

    syncLocalData() {
      this.localView = JSON.parse(JSON.stringify(this.view || {}));

      this.requestConfigUi = JSON.parse(
        JSON.stringify(this.localView.requestConfig || {})
      );

      this.filtersKey += 1;

      this.$nextTick(() => {
        const editFormRef = this.$refs.editForm as any;
        if (editFormRef?.resetValidation) editFormRef.resetValidation();
      });
    },

    async save() {
      this.loading = true;
      this.resetAlertOnly();

      try {
        const requestConfigForBackend: any = { ...(this.requestConfigUi || {}) };
        Object.keys(requestConfigForBackend).forEach((k) => {
          const v = requestConfigForBackend[k];
          if (v === null || v === undefined) delete requestConfigForBackend[k];
          else if (typeof v === 'string' && v.trim() === '') delete requestConfigForBackend[k];
          else if (Array.isArray(v) && v.length === 0) delete requestConfigForBackend[k];
        });

        const updatedView = await ViewService.update(
          this.currentTenant,
          this.view.uuid,
          String(this.localView.description || '').trim(),
          String(this.localView.entityType || '').trim(),
          String(this.localView.requestType || '').trim(),
          requestConfigForBackend
        );

        this.alertType = 'success';
        this.alertText = (this.$t('view.successUpdate') as string) || 'Saved.';
        this.alertVisible = true;

        this.justSaved = true;
        this.$emit('update', updatedView);

        setTimeout(() => {
          this.justSaved = false;
          this.dismiss();
        }, 1500);
      } catch (error: any) {
        this.alertType = 'error';
        this.alertText =
          String(this.$t('view.errorUpdate')) +
          ' ' +
          extractErrorMessage(error);
        this.alertVisible = true;
      } finally {
        this.loading = false;
      }
    },
  },

  watch: {
    localdialog(isOpen: boolean) {
      if (isOpen) {
        this.resetAlertOnly();
        this.syncLocalData();
      } else {
        this.clear();
      }
    },

    view: {
      immediate: true,
      handler() {
        if (this.localdialog && !this.justSaved) this.syncLocalData();
      },
    },

    'localView.entityType'(newEntityType: string, oldEntityType: string) {
      if (oldEntityType && newEntityType !== oldEntityType) {
        this.localView.requestType = '';
        this.requestConfigUi = {};
        this.filtersKey += 1;
      }
    },

    'localView.requestType'(newRequestType: string, oldRequestType: string) {
      if (oldRequestType && newRequestType !== oldRequestType) {
        this.requestConfigUi = {};
        this.filtersKey += 1;
      }
    },
  },

  computed: {
    currentTenant(): string {
      return (this as any).$store.state.session.selectedTenant.uuid;
    },

    requiredRule(): any[] {
      const translated = (this as any).$t?.('required') as any;
      const requiredMessage =
        typeof translated === 'string' ? translated : 'Required';
      return [(value: any) => !!String(value ?? '').trim() || requiredMessage];
    },

    requestTypesForEntity(): string[] {
      if (!this.localView.entityType) return [];
      return (
        (this.REQUEST_TYPES_BY_ENTITY as any)[this.localView.entityType] || []
      );
    },

    isReadingAggregates(): boolean {
      return (
        this.localView.entityType === 'readings' &&
        this.localView.requestType === 'reading-aggregates-collection'
      );
    },

    viewFilterConfigBase(): any[] {
      const selectedEntityType = this.localView.entityType as EntityType;

      if (selectedEntityType === 'properties') {
        return [
          { attribute: 'name', always: true },
          { attribute: 'aksId', always: true },
          { attribute: 'identifier', always: true },
          { attribute: 'street', always: true },
          { attribute: 'postalCode' },
          { attribute: 'city' },
          { attribute: 'country' },
          { attribute: 'tagIds' },
          { attribute: 'createdAt' },
        ];
      }

      if (selectedEntityType === 'devices') {
        return [
          { attribute: 'aksId', always: true },
          { attribute: 'localAksId', always: true },
          { attribute: 'description', always: true },
          { attribute: 'type', always: true },
          { attribute: 'connectorId', always: true },
          { attribute: 'propertyId', always: true },
          { attribute: 'tagIds' },
          { attribute: 'unit' },
          { attribute: 'alerting' },
          { attribute: 'isRelative' },
          { attribute: 'createdAt' },
        ];
      }

      if (selectedEntityType === 'connectors') {
        return [
          { attribute: 'name', always: true },
          { attribute: 'tagIds', always: true },
          { attribute: 'createdAt' },
        ];
      }

      if (selectedEntityType === 'readings') {
        return [
          { attribute: 'deviceId', always: true },
          { attribute: 'connectorId', always: true },
          { attribute: 'propertyId', always: true },
          { attribute: 'flag' },
          { attribute: 'createdAt' },
        ];
      }

      return [];
    },

    viewFilterConfigWithPrefill(): any[] {
      const hasValue = (value: any) => {
        if (value === null || value === undefined) return false;
        if (typeof value === 'string') return value.trim().length > 0;
        if (typeof value === 'boolean') return true;
        if (typeof value === 'number') return true;
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === 'object') return Object.keys(value).length > 0;
        return false;
      };

      return (this.viewFilterConfigBase || []).map((item: any) => {
        const attr = item.attribute;
        const currentValue = (this.requestConfigUi || {})[attr];
        return { ...item, mandatory: hasValue(currentValue) };
      });
    },
  },
});
